import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, MapPin, CalendarDays, Clock, Car, ShieldCheck, Headphones,
  BadgeIndianRupee, Timer, ArrowRight, Truck, Users, Phone, LogIn, Navigation,
  CheckCircle2, Snowflake, Download,
} from "lucide-react";
import LocationInput, { haversineKm } from "@/components/LocationInput";
import { vehicleInclusions, vehicleSeats, vehicleImage } from "@/lib/inclusions";
import { formatDateTime } from "@/lib/format";

const ROAD_FACTOR = 1.3; // straight-line → approximate road distance

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/auth";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");

const VEHICLE_ICONS = {
  Sedan: Car,
  SUV: Car,
  "Innova Crysta": Car,
  "Tempo Traveller": Truck,
};

const TRIP_TYPES = ["One Way", "Round Trip", "Rental", "Hill Station", "Cab Package"];
const RENTAL_PACKAGES = ["4hr / 40km", "8hr / 80km", "12hr / 120km"];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pricing, setPricing] = useState({ routes: [], vehicle_types: [] });
  const [fromLoc, setFromLoc] = useState(null); // {name, lat, lng}
  const [toLoc, setToLoc] = useState(null);
  const [form, setForm] = useState({
    trip_type: "One Way",
    travel_date: "",
    travel_time: "",
    return_date: "",
    rental_package: RENTAL_PACKAGES[1],
    vehicle_type: "",
    passengers: "",
  });

  const distKm = fromLoc && toLoc ? Math.round(haversineKm(fromLoc, toLoc) * ROAD_FACTOR) : null;
  const selVt = (pricing.vehicle_types || []).find((v) => v.name === form.vehicle_type);
  const estKm = distKm && form.trip_type === "Round Trip" ? distKm * 2 : distKm;
  const estFare = estKm && selVt ? Math.round(selVt.base_fare + estKm * selVt.per_km_rate) : null;
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ customer_name: "", customer_phone: "", fare_offer: "" });
  const [submitting, setSubmitting] = useState(false);
  const [installEvt, setInstallEvt] = useState(null);
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);

  const installApp = async () => {
    if (installEvt) {
      installEvt.prompt();
      await installEvt.userChoice;
      setInstallEvt(null);
    } else {
      setInstallHelpOpen(true);
    }
  };

  useEffect(() => {
    api.get("/routes/pricing").then((r) => {
      setPricing(r.data);
      if (r.data.vehicle_types?.[0]) {
        setForm((f) => ({ ...f, vehicle_type: f.vehicle_type || r.data.vehicle_types[0].name }));
      }
    }).catch(() => {});
  }, []);

  const openLead = () => {
    navigate(`/book?trip=${encodeURIComponent(form.trip_type)}${form.passengers ? `&pax=${form.passengers}` : ""}`, {
      state: { prefill: { ...form, fromLoc, toLoc } },
    });
  };

  const submitLead = async () => {
    if (!lead.customer_name || !lead.customer_phone) {
      toast.error("Please enter your name and phone");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        from_location: fromLoc.name,
        to_location: toLoc.name,
        from_lat: fromLoc.lat,
        from_lng: fromLoc.lng,
        to_lat: toLoc.lat,
        to_lng: toLoc.lng,
        distance_km: distKm,
        ...form,
        customer_name: lead.customer_name,
        customer_phone: lead.customer_phone,
        fare_offer: lead.fare_offer ? Number(lead.fare_offer) : null,
      });
      toast.success("Request received! Our team will call you shortly with the best fare.");
      setLeadOpen(false);
      setLead({ customer_name: "", customer_phone: "", fare_offer: "" });
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center shadow-md">
              <Truck className="w-5 h-5 text-slate-900" />
            </div>
            <span className="font-display text-xl font-extrabold tracking-tight text-slate-900">
              RouteWheels
            </span>
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <Button
                data-testid="header-dashboard-button"
                onClick={() => navigate(user.role === "admin" ? "/admin" : user.role === "customer" ? "/book" : "/drive")}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full"
              >
                Go to Dashboard
              </Button>
            ) : (
              <>
                {!isStandalone && (
                  <Button
                    data-testid="header-install-button"
                    onClick={installApp}
                    variant="outline"
                    className="rounded-full border-amber-400 text-amber-600 hover:bg-amber-50 font-semibold px-2.5 sm:px-4"
                    aria-label="Install App"
                  >
                    <Download className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Install App</span>
                  </Button>
                )}
                <Button
                  data-testid="header-book-button"
                  onClick={() => navigate("/book")}
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-3 sm:px-4"
                >
                  <span className="sm:hidden">Book</span><span className="hidden sm:inline">Book a Cab</span>
                </Button>
                <Button
                  data-testid="header-drive-button"
                  onClick={() => navigate("/drive")}
                  variant="outline"
                  className="rounded-full border-slate-300 hover:border-amber-500 hover:text-amber-600 px-3 sm:px-4"
                >
                  <span className="sm:hidden">Drive</span><span className="hidden sm:inline">Drive & Earn</span>
                </Button>
                <Button
                  data-testid="header-login-button"
                  onClick={startGoogleLogin}
                  variant="ghost"
                  className="rounded-full text-slate-500 hidden sm:inline-flex"
                >
                  <LogIn className="w-4 h-4 mr-2" /> Admin
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1632040804304-4094a1a63be7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-28 sm:pt-24 sm:pb-36">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 border border-amber-400/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-amber-300">
              <ShieldCheck className="w-4 h-4" /> India's Trusted Transport Network
            </span>
            <h1 className="mt-6 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-white">
              Book Outstation Cabs & <span className="text-amber-400">Transport</span> at the Best Fare
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              Compare transparent route pricing, share your trip, and get matched with verified drivers across 4000+ cities. No hidden charges.
            </p>
          </div>
        </div>
      </section>

      {/* Floating search card */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-20 relative z-20">
        <div className="bg-white rounded-2xl shadow-[0_20px_50px_rgba(15,23,42,0.15)] border border-slate-200/80 p-5 sm:p-7">
          <div className="flex flex-wrap gap-2 mb-4" data-testid="landing-trip-types">
            {TRIP_TYPES.map((t) => (
              <button
                key={t}
                data-testid={`landing-trip-type-${t.replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => setForm({ ...form, trip_type: t })}
                className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${form.trip_type === t ? "bg-amber-500 border-amber-500 text-slate-900" : "border-slate-200 text-slate-600 hover:border-amber-300"}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label="From" icon={MapPin}>
              <LocationInput
                testId="search-from-location-input"
                placeholder="Pickup location (GPS)"
                value={fromLoc}
                onSelect={setFromLoc}
                allowLocate
              />
            </Field>
            {!["Rental", "Cab Package"].includes(form.trip_type) && (
              <Field label={form.trip_type === "Hill Station" ? "To (Hill Station)" : "To"} icon={MapPin}>
                <LocationInput
                  testId="search-to-location-input"
                  placeholder="Destination location"
                  value={toLoc}
                  onSelect={setToLoc}
                />
              </Field>
            )}
            {form.trip_type === "Rental" && (
              <Field label="Rental Package" icon={Timer}>
                <Select value={form.rental_package} onValueChange={(v) => setForm({ ...form, rental_package: v })}>
                  <SelectTrigger data-testid="search-rental-package-select" className="border-0 shadow-none focus:ring-0 px-0 text-base font-medium h-auto">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RENTAL_PACKAGES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Travel Date" icon={CalendarDays}>
              <Input
                data-testid="search-travel-date-picker"
                type="date"
                value={form.travel_date}
                onChange={(e) => setForm({ ...form, travel_date: e.target.value })}
                className="border-0 shadow-none focus-visible:ring-0 px-0 text-base font-medium"
              />
            </Field>
            <Field label="Time" icon={Clock}>
              <Input
                data-testid="search-travel-time-picker"
                type="time"
                value={form.travel_time}
                onChange={(e) => setForm({ ...form, travel_time: e.target.value })}
                className="border-0 shadow-none focus-visible:ring-0 px-0 text-base font-medium"
              />
            </Field>
            {form.trip_type === "Round Trip" && (
              <Field label="Return Date" icon={CalendarDays}>
                <Input
                  data-testid="search-return-date-picker"
                  type="date"
                  value={form.return_date}
                  onChange={(e) => setForm({ ...form, return_date: e.target.value })}
                  className="border-0 shadow-none focus-visible:ring-0 px-0 text-base font-medium"
                />
              </Field>
            )}
            <Field label="No. of Pax" icon={Users}>
              <Input
                data-testid="search-pax-input"
                type="number"
                min="1"
                value={form.passengers}
                onChange={(e) => setForm({ ...form, passengers: e.target.value })}
                placeholder="e.g. 4"
                className="border-0 shadow-none focus-visible:ring-0 px-0 text-base font-medium"
              />
            </Field>
            <Field label="Vehicle" icon={Car}>
              <Select
                value={form.vehicle_type}
                onValueChange={(v) => setForm({ ...form, vehicle_type: v })}
              >
                <SelectTrigger
                  data-testid="search-vehicle-type-select"
                  className="border-0 shadow-none focus:ring-0 px-0 text-base font-medium h-auto"
                >
                  <SelectValue placeholder="Vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {(pricing.vehicle_types || []).map((v) => (
                    <SelectItem key={v.id} value={v.name}>{v.name}{vehicleSeats(v.name) ? ` · ${vehicleSeats(v.name)} seats` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className="flex items-end">
              <Button
                data-testid="search-check-fares-button"
                onClick={openLead}
                className="w-full h-[52px] rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-base shadow-lg transition-all hover:scale-[1.02]"
              >
                <Search className="w-5 h-5 mr-2" /> Check Fares
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Trust badges */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: ShieldCheck, t: "Verified Drivers", s: "Safe & comfortable" },
          { icon: Headphones, t: "24x7 Support", s: "Always available" },
          { icon: BadgeIndianRupee, t: "Transparent Pricing", s: "No hidden charges" },
          { icon: Timer, t: "On-time Service", s: "Reliable every trip" },
        ].map((b) => (
          <div key={b.t} className="bg-white rounded-xl border border-slate-200/80 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <b.icon className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-display font-semibold text-sm text-slate-900">{b.t}</p>
              <p className="text-xs text-slate-500">{b.s}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Fleet — vehicle cards like HurryUp Cabs */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14" data-testid="fleet-section">
        <div className="mb-8">
          <p className="text-xs font-medium uppercase tracking-wider text-amber-600">Our Fleet</p>
          <h2 className="mt-1 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            Choose Your Ride
          </h2>
          <p className="mt-2 text-slate-500 text-sm sm:text-base">
            Every vehicle is AC — fuel, toll tax &amp; driver perks always included.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {(pricing.vehicle_types || []).map((v) => (
            <div key={v.id} data-testid={`fleet-card-${v.name.replace(/\s/g, "-").toLowerCase()}`} className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_2px_8px_rgba(15,23,42,0.06)] p-4 flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-all">
              {vehicleImage(v.name) ? (
                <img src={vehicleImage(v.name)} alt={v.name} className="w-full h-24 object-cover rounded-xl" loading="lazy" />
              ) : (
                <div className="w-full h-24 rounded-xl bg-slate-100 flex items-center justify-center"><Car className="w-8 h-8 text-slate-300" /></div>
              )}
              <p className="mt-3 font-display font-bold text-slate-900">{v.name}</p>
              <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-600">
                {vehicleSeats(v.name) && (
                  <span data-testid={`fleet-seats-${v.name.replace(/\s/g, "-").toLowerCase()}`} className="inline-flex items-center gap-1 font-medium"><Users className="w-3.5 h-3.5 text-amber-500" /> {vehicleSeats(v.name)} seats</span>
                )}
                <span className="inline-flex items-center gap-1 font-medium"><Snowflake className="w-3.5 h-3.5 text-sky-500" /> AC</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {vehicleInclusions(v.name).map((inc) => (
                  <span key={inc} className="rounded-full bg-emerald-50 border border-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">{inc}</span>
                ))}
              </div>
              <p className="mt-auto pt-3 font-display font-extrabold text-slate-900">₹{v.per_km_rate}<span className="text-xs font-medium text-slate-500">/km · base {inr(v.base_fare)}</span></p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing table */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="mb-8">
          <p className="text-xs font-medium uppercase tracking-wider text-amber-600">Transparent Rates</p>
          <h2 className="mt-1 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            Popular Route Pricing
          </h2>
          <p className="mt-2 text-slate-500 text-sm sm:text-base">
            Estimated one-way fares. Final fare confirmed after you share trip details.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_2px_8px_rgba(15,23,42,0.06)] overflow-hidden">
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full min-w-[640px]" data-testid="route-pricing-table">
              <thead>
                <tr className="bg-slate-900 text-white text-left">
                  <th className="py-4 px-5 font-display text-sm font-semibold">Route</th>
                  {(pricing.vehicle_types || []).map((v) => (
                    <th key={v.id} className="py-4 px-5 font-display text-sm font-semibold whitespace-nowrap">
                      <div className="flex items-center gap-2.5">
                        {vehicleImage(v.name) && (
                          <img src={vehicleImage(v.name)} alt={v.name} data-testid={`vehicle-photo-${v.name.replace(/\s/g, "-").toLowerCase()}`} className="w-12 h-9 rounded-md object-cover border border-white/20" loading="lazy" />
                        )}
                        <div>
                          {v.name}
                          {vehicleSeats(v.name) && (
                            <span data-testid={`seats-${v.name.replace(/\s/g, "-").toLowerCase()}`} className="block text-[11px] font-medium font-body text-amber-300 mt-0.5">{vehicleSeats(v.name)} seats</span>
                          )}
                        </div>
                      </div>
                      <span className="block text-[11px] font-medium font-body text-slate-300 mt-1">
                        ₹{v.per_km_rate}/km · base {inr(v.base_fare)}
                      </span>
                      <span data-testid={`inclusions-${v.name.replace(/\s/g, "-").toLowerCase()}`} className="block text-[10px] font-normal font-body text-emerald-300/90 mt-1 whitespace-normal leading-snug">
                        Incl: {vehicleInclusions(v.name).join(" · ")}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pricing.routes.map((r, i) => {
                  const Icon = VEHICLE_ICONS[form.vehicle_type] || Car;
                  return (
                    <tr
                      key={i}
                      data-testid="pricing-row"
                      className="border-t border-slate-100 hover:bg-amber-50/40 transition-colors"
                    >
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-2 font-semibold text-slate-900">
                          <span>{r.from_location}</span>
                          <ArrowRight className="w-4 h-4 text-amber-500" />
                          <span>{r.to_location}</span>
                        </div>
                        <span className="text-xs text-slate-400">{r.distance_km} km approx</span>
                      </td>
                      {pricing.vehicle_types.map((v) => (
                        <td key={v.id} className="py-4 px-5">
                          <span className="font-display font-bold text-slate-900">{inr(r.prices[v.name])}</span>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <p data-testid="pricing-toll-note" className="mt-3 text-xs text-slate-500 flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          All fares shown include toll &amp; state taxes. Rates: base flat fare + per-km — no hidden charges.
        </p>
      </section>

      {/* Driver CTA */}
      <section className="bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">Drive More. Earn More.</h2>
            <p className="mt-2 text-slate-300 max-w-xl">
              Join as a driver partner. Get approved by our team and receive live leads with upfront prices — right on your phone.
            </p>
          </div>
          <Button
            data-testid="become-driver-button"
            onClick={() => navigate("/drive")}
            className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8 h-12 text-base shadow-lg"
          >
            Become a Driver <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 text-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="font-display font-semibold text-white">RouteWheels</span>
          <span>© 2026 RouteWheels · India's Transport Lead Network</span>
        </div>
      </footer>

      {/* Install help dialog (mobile browsers without native prompt) */}
      <Dialog open={installHelpOpen} onOpenChange={setInstallHelpOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Install RouteWheels App</DialogTitle>
            <DialogDescription>Add RouteWheels to your home screen for one-tap booking.</DialogDescription>
          </DialogHeader>
          {isIOS ? (
            <ol className="space-y-2.5 text-sm text-slate-700 list-decimal list-inside" data-testid="install-steps-ios">
              <li>Open this site in <b>Safari</b>.</li>
              <li>Tap the <b>Share</b> button (square with an upward arrow) at the bottom.</li>
              <li>Scroll down and tap <b>"Add to Home Screen"</b>.</li>
              <li>Tap <b>Add</b> — the RouteWheels icon appears on your home screen.</li>
            </ol>
          ) : (
            <ol className="space-y-2.5 text-sm text-slate-700 list-decimal list-inside" data-testid="install-steps-android">
              <li>Open this site in <b>Chrome</b> (tap "Open in browser" if you're inside WhatsApp/Instagram).</li>
              <li>Tap the <b>⋮ menu</b> (top-right corner).</li>
              <li>Tap <b>"Install app"</b> or <b>"Add to Home screen"</b>.</li>
              <li>Confirm — the RouteWheels icon appears on your home screen.</li>
            </ol>
          )}
        </DialogContent>
      </Dialog>

      {/* Lead capture dialog */}
      <Dialog open={leadOpen} onOpenChange={setLeadOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lead-capture-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Almost there!</DialogTitle>
            <DialogDescription>
              {fromLoc?.name} → {toLoc?.name} · {formatDateTime(form.travel_date, form.travel_time)} · {form.vehicle_type}
            </DialogDescription>
          </DialogHeader>
          {selVt && (
            <div
              data-testid="lead-distance-estimate"
              className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 space-y-2.5"
            >
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5 font-medium text-slate-700">
                  <Navigation className="w-4 h-4 text-amber-600" /> {distKm ? `~${distKm} km` : "Trip distance"}
                </span>
                <span className="font-semibold text-slate-700">
                  ₹{selVt.per_km_rate}/km · Base {inr(selVt.base_fare)}
                </span>
              </div>
              {estFare && (
                <div className="flex items-center justify-between border-t border-amber-200/70 pt-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Estimated fare</span>
                  <span className="font-display font-extrabold text-lg text-slate-900">{inr(estFare)}</span>
                </div>
              )}
              <p data-testid="lead-toll-note" className="text-xs font-medium text-emerald-700 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Toll &amp; state taxes included — no hidden charges
              </p>
            </div>
          )}
          <div className="space-y-4 pt-2">
            <div>
              <Label htmlFor="lname" className="text-sm">Your Name</Label>
              <Input
                id="lname"
                data-testid="lead-customer-name-input"
                placeholder="Full name"
                value={lead.customer_name}
                onChange={(e) => setLead({ ...lead, customer_name: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="lphone" className="text-sm">Phone Number</Label>
              <div className="mt-1.5 flex items-center rounded-md border border-input px-3 focus-within:ring-2 focus-within:ring-amber-500">
                <span className="text-slate-500 text-sm mr-2">+91</span>
                <Input
                  id="lphone"
                  data-testid="lead-customer-phone-input"
                  placeholder="98765 43210"
                  value={lead.customer_phone}
                  onChange={(e) => setLead({ ...lead, customer_phone: e.target.value })}
                  className="border-0 shadow-none focus-visible:ring-0 px-0"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="loffer" className="text-sm">Offer Your Fare (optional)</Label>
              <Input
                id="loffer"
                data-testid="lead-customer-offer-input"
                type="number"
                placeholder="e.g. 3500"
                value={lead.fare_offer}
                onChange={(e) => setLead({ ...lead, fare_offer: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <Button
              data-testid="lead-capture-submit-button"
              onClick={submitLead}
              disabled={submitting}
              className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
            >
              <Phone className="w-4 h-4 mr-2" /> {submitting ? "Sending..." : "Get Best Fare Call"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-slate-200 px-4 py-2 focus-within:border-amber-500 focus-within:ring-2 focus-within:ring-amber-500/30 transition-all">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-500">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      {children}
    </div>
  );
}
